import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useLocale } from '@/i18n/LocaleContext';
import { localizedPath } from '@/i18n/locales';
import {
  useConnectionsQuery,
  useLazyTripByRouteQuery,
  useLocationsQuery,
} from '@/services/api/catalogApi';
import type { Location } from '@/services/api/types';
import { LocationField } from './LocationField';

/**
 * Pickup and destination picker.
 *
 * Works from either end, as the requirements ask: choosing a pickup narrows the
 * destination list to places actually served from it, and choosing a destination
 * first narrows the pickup list to places that actually reach it. Whichever field
 * the customer fills first becomes the anchor.
 */
export function BookingSearch() {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();

  const [pickup, setPickup] = useState<Location | null>(null);
  const [destination, setDestination] = useState<Location | null>(null);
  const [routeMissing, setRouteMissing] = useState(false);

  const [findTrip, { isFetching: resolvingTrip }] = useLazyTripByRouteQuery();

  // The anchor is whichever end was chosen first; the other list is derived from it.
  const anchor: 'pickup' | 'destination' | null = pickup
    ? 'pickup'
    : destination
      ? 'destination'
      : null;

  const allPickups = useLocationsQuery('PICKUP', { skip: anchor === 'destination' });
  const allDestinations = useLocationsQuery('DESTINATION', { skip: anchor === 'pickup' });

  const destinationsFromPickup = useConnectionsQuery(
    { slug: pickup?.slug ?? '', role: 'PICKUP' },
    { skip: !pickup },
  );
  const pickupsToDestination = useConnectionsQuery(
    { slug: destination?.slug ?? '', role: 'DESTINATION' },
    { skip: !destination || anchor === 'pickup' },
  );

  const pickupOptions = useMemo<Location[]>(
    () => (anchor === 'destination' ? (pickupsToDestination.data ?? []) : (allPickups.data ?? [])),
    [anchor, pickupsToDestination.data, allPickups.data],
  );

  const destinationOptions = useMemo<Location[]>(
    () =>
      anchor === 'pickup'
        ? (destinationsFromPickup.data ?? [])
        : (allDestinations.data ?? []),
    [anchor, destinationsFromPickup.data, allDestinations.data],
  );

  const loadingPickups = allPickups.isLoading || pickupsToDestination.isLoading;
  const loadingDestinations = allDestinations.isLoading || destinationsFromPickup.isLoading;
  const failed =
    allPickups.isError ||
    allDestinations.isError ||
    destinationsFromPickup.isError ||
    pickupsToDestination.isError;

  const ready = pickup !== null && destination !== null;

  /**
   * Resolves the chosen pair to a real trip before navigating.
   *
   * The slug is whatever the backend says it is — deriving it from the two
   * location slugs would break the moment an administrator renamed a route.
   */
  async function search() {
    if (!pickup || !destination) return;

    setRouteMissing(false);
    const result = await findTrip({ origin: pickup.slug, destination: destination.slug });

    if (result.data) {
      navigate(localizedPath('trips', locale, result.data.trip.slug));
    } else {
      setRouteMissing(true);
    }
  }

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
      <Stack spacing={2}>
        {failed && <Alert severity="error">{t('common.error')}</Alert>}
        {routeMissing && <Alert severity="info">{t('booking.noRoute')}</Alert>}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <LocationField
            label={t('booking.pickup')}
            value={pickup}
            options={pickupOptions}
            loading={loadingPickups}
            onChange={(next) => {
              setPickup(next);
              // Clearing the anchor releases the other field back to the full list.
              if (!next) setDestination((current) => (anchor === 'pickup' ? null : current));
            }}
          />

          <LocationField
            label={t('booking.destination')}
            value={destination}
            options={destinationOptions}
            loading={loadingDestinations}
            onChange={setDestination}
          />

          <Button
            variant="contained"
            size="large"
            disabled={!ready || resolvingTrip}
            onClick={() => void search()}
            sx={{ minWidth: { md: 200 }, py: { md: 1.75 } }}
          >
            {t('booking.search')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default BookingSearch;
